import { DiscoveryApi, FetchApi } from '@backstage/core-plugin-api';
import type {
  AccessRestriction,
  AvailabilityResponse,
  CreateSiteRequest,
  CreateSiteResponse,
  DeleteSiteResult,
  DetachDomainResult,
  AdminAnalytics,
  AdminCustomDomainRequest,
  AnalyticsRange,
  DnsCleanupNotice,
  CreateDomainResult,
  CustomDomainRequest,
  MeResponse,
  PluginConfig,
  PresignResponse,
  SiteRecord,
  PublicDomain,
  R2Object,
  RedeployWorkerResult,
  RedeployWorkersReport,
  SiteAnalytics,
  SiteLifecycle,
  SiteTag,
} from '@internal/plugin-spark-common';
import type { SparkApi } from './sparkApi';

const UPLOAD_CONCURRENCY = 6;
const PUT_RETRY_DELAYS_MS = [1_000, 3_000, 6_000, 10_000, 15_000];

const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

/** PUT to a presigned URL; retry network/5xx (e.g. CORS lag). Returns error string or undefined. */
async function putWithRetry(
  url: string,
  headers: Record<string, string>,
  file: File,
): Promise<string | undefined> {
  let lastError = 'unknown error';
  for (let attempt = 0; attempt <= PUT_RETRY_DELAYS_MS.length; attempt++) {
    if (attempt > 0) await sleep(PUT_RETRY_DELAYS_MS[attempt - 1]);
    try {
      // Plain fetch — never send Backstage/Access credentials to R2.
      const put = await fetch(url, { method: 'PUT', headers, body: file });
      if (put.ok) return undefined;
      lastError = `upload failed (HTTP ${put.status})`;
      if (put.status < 500) return lastError;
    } catch (err: any) {
      // TypeError/"Failed to fetch": CORS not yet propagated, DNS, or network.
      lastError = err.message ?? String(err);
    }
  }
  return `${lastError} (after ${PUT_RETRY_DELAYS_MS.length + 1} attempts)`;
}

/** Object key for a file, preserving folder structure from directory uploads. */
function fileKey(file: File): string {
  return (file as File & { webkitRelativePath?: string }).webkitRelativePath || file.name;
}

/**
 * If every file shares a single top-level directory (e.g. selecting a `mysite/`
 * folder yields `mysite/index.html`, `mysite/css/app.css`), return that prefix
 * so it can be stripped — publishing the folder's *contents* at the site root,
 * matching wrangler/Cloudflare Pages behavior. Returns '' when there is no
 * common top-level folder (loose files or multiple top dirs).
 */
function commonTopDir(keys: string[]): string {
  if (keys.length === 0) return '';
  const slash = keys[0].indexOf('/');
  if (slash === -1) return '';
  const prefix = keys[0].slice(0, slash + 1);
  return keys.every(k => k.startsWith(prefix)) ? prefix : '';
}

type JsonRequestInit = {
  method?: string;
  body?: unknown;
  /** When set, 207 Multi-Status is returned as data instead of throwing. */
  acceptPartial?: boolean;
};

/** HTTP client backing all Spark capability apiRefs. */
export class R2HostingClient implements SparkApi {
  constructor(
    private readonly discoveryApi: DiscoveryApi,
    private readonly fetchApi: FetchApi,
  ) {}

  private async baseUrl(): Promise<string> {
    return this.discoveryApi.getBaseUrl('spark');
  }

  private static extractError(data: any, fallback: string): string {
    if (typeof data?.error === 'string') return data.error;
    if (typeof data?.message === 'string') return data.message;
    return fallback;
  }

  private async requestJson<T>(
    path: string,
    init: JsonRequestInit = {},
  ): Promise<{ status: number; data: T }> {
    const base = await this.baseUrl();
    const res = await this.fetchApi.fetch(`${base}${path}`, {
      method: init.method ?? 'GET',
      headers: init.body !== undefined ? { 'Content-Type': 'application/json' } : undefined,
      body: init.body !== undefined ? JSON.stringify(init.body) : undefined,
    });
    const data = await res.json();
    if (init.acceptPartial && res.status === 207) {
      return { status: res.status, data: data as T };
    }
    if (!res.ok) {
      throw new Error(R2HostingClient.extractError(data, res.statusText));
    }
    return { status: res.status, data: data as T };
  }

  private async get<T>(path: string): Promise<T> {
    return (await this.requestJson<T>(path)).data;
  }

  private async post<T>(path: string, body?: unknown): Promise<T> {
    return (await this.requestJson<T>(path, { method: 'POST', body })).data;
  }

  private async put<T>(path: string, body?: unknown): Promise<T> {
    return (await this.requestJson<T>(path, { method: 'PUT', body })).data;
  }

  private async del<T>(path: string, body?: unknown): Promise<T> {
    return (await this.requestJson<T>(path, { method: 'DELETE', body })).data;
  }

  private sitePath(siteName: string, suffix = ''): string {
    return `/sites/${encodeURIComponent(siteName)}${suffix}`;
  }

  async getConfig(): Promise<PluginConfig> {
    return this.get<PluginConfig>('/config');
  }

  async createSite(req: CreateSiteRequest): Promise<CreateSiteResponse> {
    // Provision only — files are uploaded directly to R2 afterward (ADR 0001).
    const base = await this.baseUrl();
    const res = await this.fetchApi.fetch(`${base}/sites`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name: req.name, displayName: req.displayName }),
    });
    const data = await res.json();
    if (!res.ok) {
      let message = R2HostingClient.extractError(data, res.statusText);
      if (data?.rollback?.errors?.length) {
        message += ` Automatic cleanup hit problems: ${data.rollback.errors.join('; ')}`;
      } else if (data?.rollback) {
        message += ' Any partially-created resources were rolled back automatically.';
      }
      throw new Error(message);
    }
    return data as CreateSiteResponse;
  }

  async listSites(): Promise<SiteRecord[]> {
    return this.get<SiteRecord[]>('/sites');
  }

  async listAllSites(): Promise<SiteRecord[]> {
    return this.get<SiteRecord[]>('/sites/all');
  }

  async getSite(name: string): Promise<SiteRecord> {
    return this.get<SiteRecord>(this.sitePath(name));
  }

  async listObjects(siteName: string): Promise<R2Object[]> {
    return this.get<R2Object[]>(this.sitePath(siteName, '/objects'));
  }

  /**
   * Uploads files directly to R2 using backend-issued presigned PUT URLs
   * (ADR 0001): presign → parallel PUTs (bounded) → complete. The backend never
   * touches the bytes.
   */
  async uploadFiles(siteName: string, files: File[]): Promise<{ uploaded: number }> {
    if (!files.length) return { uploaded: 0 };

    const rawKeys = files.map(fileKey);
    const prefix = commonTopDir(rawKeys);
    const entries = files.map((file, i) => ({
      file,
      key: prefix ? rawKeys[i].slice(prefix.length) : rawKeys[i],
    }));
    const { uploads } = await this.post<PresignResponse>(
      this.sitePath(siteName, '/uploads/presign'),
      {
        files: entries.map(e => ({
          key: e.key,
          contentType: e.file.type || undefined,
          size: e.file.size,
        })),
      },
    );
    const byKey = new Map(uploads.map(u => [u.key, u]));

    const errors: string[] = [];
    let cursor = 0;
    const worker = async (): Promise<void> => {
      while (cursor < entries.length) {
        const { file, key } = entries[cursor++];
        const target = byKey.get(key);
        if (!target) {
          errors.push(`${key}: no upload URL was issued`);
          continue;
        }
        const failure = await putWithRetry(target.url, target.headers, file);
        if (failure) errors.push(`${key}: ${failure}`);
      }
    };

    await Promise.all(
      Array.from({ length: Math.min(UPLOAD_CONCURRENCY, entries.length) }, () => worker()),
    );

    // Best-effort: refresh the owner's cached listing regardless of partial errors.
    const publication = await this.completeUpload(siteName);

    if (errors.length) {
      throw new Error(`Some files failed to upload: ${errors.join('; ')}`);
    }
    return { uploaded: files.length, ...publication };
  }

  private async completeUpload(
    siteName: string,
  ): Promise<{ lastPublishedAt?: string; lastPublishedBy?: string }> {
    try {
      return await this.post(this.sitePath(siteName, '/uploads/complete'));
    } catch {
      // Cache invalidation is best-effort; a stale count self-heals on TTL.
      return {};
    }
  }

  async deleteObjects(
    siteName: string,
    keys: string[],
  ): Promise<{ deleted: number; lastPublishedAt?: string; lastPublishedBy?: string }> {
    return this.del(this.sitePath(siteName, '/objects'), { keys });
  }

  async checkAvailability(name: string): Promise<AvailabilityResponse> {
    return this.get<AvailabilityResponse>(this.sitePath(name, '/availability'));
  }

  async deleteSite(name: string): Promise<DeleteSiteResult> {
    const { status, data } = await this.requestJson<{
      deleted?: string;
      errors?: string[];
    }>(this.sitePath(name), { method: 'DELETE', acceptPartial: true });
    if (status === 207) {
      return { deleted: data?.deleted ?? name, partial: true, errors: data?.errors ?? [] };
    }
    return { deleted: data?.deleted ?? name, partial: false };
  }

  async createDomain(
    siteName: string,
    fqdn: string,
    restriction?: AccessRestriction,
  ): Promise<CreateDomainResult> {
    return this.post<CreateDomainResult>(this.sitePath(siteName, '/domains'), {
      fqdn,
      restriction,
    });
  }

  async approveCustomDomain(siteName: string, domain: string): Promise<PublicDomain> {
    return this.post<PublicDomain>(
      this.sitePath(
        siteName,
        `/custom-domain-requests/${encodeURIComponent(domain)}/approve`,
      ),
    );
  }

  async rejectCustomDomain(
    siteName: string,
    domain: string,
    reason?: string,
  ): Promise<CustomDomainRequest> {
    return this.post<CustomDomainRequest>(
      this.sitePath(
        siteName,
        `/custom-domain-requests/${encodeURIComponent(domain)}/reject`,
      ),
      reason ? { reason } : {},
    );
  }

  async dismissRejectedDomain(siteName: string, domain: string): Promise<void> {
    await this.post(
      this.sitePath(siteName, `/custom-domain-requests/${encodeURIComponent(domain)}/dismiss`),
    );
  }

  async listCustomDomainRequests(): Promise<AdminCustomDomainRequest[]> {
    return this.get<AdminCustomDomainRequest[]>('/admin/custom-domain-requests');
  }

  async listDnsCleanup(): Promise<DnsCleanupNotice[]> {
    return this.get<DnsCleanupNotice[]>('/admin/dns-cleanup');
  }

  async dismissDnsCleanup(domain: string): Promise<void> {
    await this.post(`/admin/dns-cleanup/${encodeURIComponent(domain)}/dismiss`);
  }

  async refreshDomain(siteName: string, domain: string): Promise<PublicDomain> {
    return this.post<PublicDomain>(
      this.sitePath(siteName, `/domains/${encodeURIComponent(domain)}/refresh`),
    );
  }

  async detachDomain(
    siteName: string,
    domain: string,
  ): Promise<DetachDomainResult> {
    const { status, data } = await this.requestJson<{
      detached?: string;
      errors?: string[];
    }>(this.sitePath(siteName, `/domains/${encodeURIComponent(domain)}`), {
      method: 'DELETE',
      acceptPartial: true,
    });
    if (status === 207) {
      return {
        detached: data?.detached ?? domain,
        partial: true,
        errors: data?.errors ?? [],
      };
    }
    return { detached: data?.detached ?? domain, partial: false };
  }

  async setApiOrigin(
    siteName: string,
    apiOrigin: string | null,
  ): Promise<{ apiOrigin: string | null }> {
    return this.put<{ apiOrigin: string | null }>(this.sitePath(siteName, '/api-origin'), {
      apiOrigin,
    });
  }

  async setDefaultPage(
    siteName: string,
    defaultPage: string | null,
  ): Promise<{ defaultPage: string | null }> {
    return this.put<{ defaultPage: string | null }>(
      this.sitePath(siteName, '/default-page'),
      { defaultPage },
    );
  }

  async setSiteProfile(
    siteName: string,
    profile: {
      description?: string;
      lifecycle: SiteLifecycle;
      tags: SiteTag[];
    },
  ): Promise<{
    description: string | null;
    lifecycle: SiteLifecycle;
    tags: SiteTag[];
    lastPublishedAt?: string;
    lastPublishedBy?: string;
  }> {
    return this.put(this.sitePath(siteName, '/profile'), profile);
  }

  async setCollaborators(
    siteName: string,
    collaborators: string[],
    owner?: string,
  ): Promise<{ owner: string; collaborators: string[] }> {
    return this.put<{ owner: string; collaborators: string[] }>(
      this.sitePath(siteName, '/collaborators'),
      owner ? { collaborators, owner } : { collaborators },
    );
  }

  async getMe(): Promise<MeResponse> {
    return this.get<MeResponse>('/me');
  }

  async redeployWorker(siteName: string): Promise<RedeployWorkerResult> {
    return this.post<RedeployWorkerResult>(
      this.sitePath(siteName, '/worker/redeploy'),
    );
  }

  async redeployAllWorkers(): Promise<RedeployWorkersReport> {
    const { data } = await this.requestJson<RedeployWorkersReport>(
      '/admin/workers/redeploy',
      { method: 'POST', acceptPartial: true },
    );
    return data;
  }

  async getSiteAnalytics(
    siteName: string,
    range: AnalyticsRange,
  ): Promise<SiteAnalytics> {
    return this.get<SiteAnalytics>(
      this.sitePath(siteName, `/analytics?range=${range}`),
    );
  }

  async getAdminAnalytics(range: AnalyticsRange): Promise<AdminAnalytics> {
    return this.get<AdminAnalytics>(`/admin/analytics?range=${range}`);
  }

  async updateDomainAccess(
    siteName: string,
    domain: string,
    restriction: AccessRestriction,
  ): Promise<PublicDomain> {
    return this.put<PublicDomain>(
      this.sitePath(siteName, `/domains/${encodeURIComponent(domain)}/access`),
      { restriction },
    );
  }
}
